// FilePreviewDialog (E7.2.1 → estendido em E7.2.2) — modal orquestrador do preview.
//
// Decisões congeladas: modal Dialog (não inline na bolha) + híbrido lazy (só
// chama o dispatcher ao abrir). Ao abrir: `useFilePreview(enabled: open)` →
// usa a `url` FRESCA + `render_hint` da resposta e roteia pro renderer certo.
// `PdfPreview` é lazy (bundle react-pdf só no 1º PDF).
//
// E7.2.2: Office `pending` agora faz POLLING (spinner "convertendo") até virar
// `ready` (→ PDF). Estourado o teto de polls (`pollExhausted`) ou erro genérico
// → "não foi possível gerar" + Baixar + Tentar novamente. 413 (Office > 50MB)
// → aviso de tamanho, terminal (sem retry).
import { lazy, Suspense, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Download, Loader2, RefreshCw } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useFilePreview } from "@/hooks/useFilePreview";
import { MAX_PREVIEW_MB } from "@/services/filePreviewService";
import { CsvPreview } from "./CsvPreview";
// `MarkdownPreview` é lazy pelo mesmo motivo do `PdfPreview` logo abaixo: o
// `react-markdown` são ~310 KB comprimidos, o MAIOR chunk do app depois do
// entry. Era import ESTÁTICO aqui, e como este diálogo está no grafo ansioso
// (App → AtendimentoPage → AttachmentMessageBubble → FilePreviewDialog), TODO
// usuário baixava o renderizador de markdown no boot — inclusive na tela de
// login do parceiro. Quem adiou o PDF esqueceu o markdown, que é maior.

const PdfPreview = lazy(() => import("./PdfPreview"));
const MarkdownPreview = lazy(() =>
  import("./MarkdownPreview").then((m) => ({ default: m.MarkdownPreview }))
);

export interface FilePreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** storage_key (URL-encoded) derivado de content.url; null → não abre dispatcher. */
  storageKey: string | null;
  filename?: string;
  /** content.url — fallback do botão Baixar quando o dispatcher não retornou url. */
  downloadUrl?: string;
}

function Centered({ children, testid }: { children: ReactNode; testid?: string }) {
  return (
    <div
      className="flex min-h-[200px] items-center justify-center text-sm text-muted-foreground"
      data-testid={testid}
    >
      {children}
    </div>
  );
}

/** Botão Baixar reutilizável (abre o original em nova aba / dispara download). */
function DownloadButton({ href, filename }: { href?: string; filename?: string }) {
  if (!href) return null;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" download={filename}>
      <Button variant="outline" size="sm">
        <Download className="mr-2 h-4 w-4" /> Baixar
      </Button>
    </a>
  );
}

/**
 * Estado de mensagem central (falha / tamanho) com botões de ação. Usado quando
 * o preview não pôde ser renderizado mas ainda há o que oferecer (baixar/retry).
 */
function MessageState({
  testid,
  title,
  hint,
  children,
}: {
  testid: string;
  title: string;
  hint?: string;
  children?: ReactNode;
}) {
  return (
    <div
      className="flex min-h-[200px] flex-col items-center justify-center gap-3 px-4 text-center"
      data-testid={testid}
    >
      <div className="text-sm text-muted-foreground">
        <div className="font-medium text-foreground">{title}</div>
        {hint && <div className="mt-1">{hint}</div>}
      </div>
      {children && (
        <div className="flex flex-wrap items-center justify-center gap-2">{children}</div>
      )}
    </div>
  );
}

/** Teto de caracteres do preview de JSON/texto (evita travar a aba com arquivos grandes). */
const TEXT_MAX_CHARS = 200_000;

/** Render simples de JSON (pretty) ou texto plano buscado da url. */
function JsonTextPreview({ url }: { url: string }) {
  const [state, setState] = useState<{ loading: boolean; error: boolean; text: string }>({
    loading: true,
    error: false,
    text: "",
  });

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: false }));
    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return r.text();
      })
      .then((text) => {
        if (alive) setState({ loading: false, error: false, text });
      })
      .catch(() => {
        if (alive) setState({ loading: false, error: true, text: "" });
      });
    return () => {
      alive = false;
    };
  }, [url]);

  if (state.loading)
    return (
      <Centered testid="text-loading">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Carregando…
      </Centered>
    );
  if (state.error) return <Centered testid="preview-error">Erro ao carregar.</Centered>;

  const truncated = state.text.length > TEXT_MAX_CHARS;
  const source = truncated ? state.text.slice(0, TEXT_MAX_CHARS) : state.text;
  let pretty = source;
  if (!truncated) {
    try {
      pretty = JSON.stringify(JSON.parse(source), null, 2);
    } catch {
      // Não é JSON válido → mostra o texto cru.
    }
  }
  return (
    <>
      <pre
        className="max-h-[70vh] overflow-auto rounded border bg-muted/30 p-3 text-xs"
        data-testid="text-preview"
      >
        {pretty}
      </pre>
      {truncated && (
        <p className="mt-2 text-xs text-muted-foreground" data-testid="text-truncated">
          Preview truncado. Baixe o arquivo para ver o conteúdo completo.
        </p>
      )}
    </>
  );
}

export function FilePreviewDialog({
  open,
  onOpenChange,
  storageKey,
  filename,
  downloadUrl,
}: FilePreviewDialogProps) {
  const { data, isLoading, isError, isPolling, pollExhausted, isTooLarge, retry } = useFilePreview(
    storageKey,
    { enabled: open }
  );

  // ready (Office convertido) sempre renderiza PDF; senão usa o render_hint.
  const hint = data?.render_hint ?? (data?.status === "ready" ? "pdf" : undefined);
  const url = data?.url ?? undefined;
  const downloadHref = url || downloadUrl;

  const body = (): ReactNode => {
    // 413 — Office maior que o limite. Terminal: retry daria o mesmo 413.
    if (isTooLarge)
      return (
        <MessageState
          testid="preview-too-large"
          title="Arquivo grande demais para preview"
          hint={`Acima de ${MAX_PREVIEW_MB} MB. Baixe o arquivo para visualizá-lo.`}
        >
          <DownloadButton href={downloadHref} filename={filename} />
        </MessageState>
      );
    // Falha = teto de polling estourado OU erro genérico (404/415/503/network).
    // O backend nunca grava `failed`, então "não converteu a tempo" cai aqui.
    if (pollExhausted || isError)
      return (
        <MessageState
          testid="preview-failed"
          title="Não foi possível gerar o preview"
          hint="O arquivo pode ser muito complexo ou a conversão demorou demais."
        >
          <DownloadButton href={downloadHref} filename={filename} />
          <Button variant="default" size="sm" onClick={retry} data-testid="preview-retry-btn">
            <RefreshCw className="mr-2 h-4 w-4" /> Tentar novamente
          </Button>
        </MessageState>
      );
    if (isLoading)
      return (
        <Centered testid="preview-loading">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Carregando preview…
        </Centered>
      );
    if (!data)
      return <Centered testid="preview-error">Não foi possível carregar o preview.</Centered>;
    // Office convertendo — polling em andamento (spinner, sem loop infinito: o
    // hook para no teto de polls e cai no estado de falha acima).
    if (isPolling || data.status === "pending")
      return (
        <Centered testid="preview-converting">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Convertendo documento para visualização…
        </Centered>
      );
    if (!url)
      return <Centered testid="preview-error">Não foi possível carregar o preview.</Centered>;

    switch (hint) {
      case "pdf":
        return (
          <Suspense
            fallback={
              <Centered>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Carregando visualizador…
              </Centered>
            }
          >
            <PdfPreview url={url} filename={filename} />
          </Suspense>
        );
      case "csv":
        return <CsvPreview url={url} filename={filename} />;
      case "markdown":
        return (
          <Suspense
            fallback={
              <Centered>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Carregando visualizador…
              </Centered>
            }
          >
            <MarkdownPreview url={url} filename={filename} />
          </Suspense>
        );
      case "json":
      case "text":
        return <JsonTextPreview url={url} />;
      case "image":
        return (
          <img
            src={url}
            alt={filename || "imagem"}
            className="mx-auto max-h-[70vh] object-contain"
          />
        );
      default:
        return (
          <Centered testid="preview-unsupported">
            Pré-visualização não disponível para este arquivo.
          </Centered>
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="file-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2 pr-6">
            <span className="truncate">{filename || "Visualização"}</span>
            {downloadHref && (
              <a href={downloadHref} target="_blank" rel="noopener noreferrer" download={filename}>
                <Button variant="outline" size="sm">
                  <Download className="mr-2 h-4 w-4" /> Baixar
                </Button>
              </a>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Pré-visualização do anexo {filename || ""}
          </DialogDescription>
        </DialogHeader>
        {body()}
      </DialogContent>
    </Dialog>
  );
}

export default FilePreviewDialog;
